import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  CaptchaContainer,
  CaptchaWrapper,
  ImageContainer,
  PuzzlePiece,
  SliderContainer,
  SliderTrack,
  Slider,
  SliderText,
  LoadingContainer,
  ErrorMessage,
  SliderPrompt,
  CaptchaTitle
} from './SlideCaptcha.styles';
import ActivityIndicator from '../ActivityIndicator/ActivityIndicator';

export interface CaptchaData {
  image: string;
  thumb: string;
  thumbX: number;
  thumbY: number;
  thumbWidth: number;
  thumbHeight: number;
  key: string;
}

export interface SlideCaptchaProps {
  data: CaptchaData | null;
  onValidate: (point: { x: number; y: number }, key: string, duration: number) => Promise<boolean>;
  onRefresh?: () => void;
  loading?: boolean;
  error?: string;
  /** Text shown as the captcha title */
  titleText: string;
  /** Text shown on the retry button in error state */
  retryText: string;
  /** Text shown when no captcha data is available */
  noDataAvailableText: string;
  /** Alt text for the background image */
  backgroundAltText: string;
  /** Alt text for the puzzle piece image */
  pieceAltText: string;
  /** Text shown on the slider handle while validating */
  validatingText: string;
  /** Text shown on the slider handle normally */
  sliderHandleText: string;
  /** Instruction text shown inside the slider track */
  instructionText: string;
}

const SlideCaptcha: React.FC<SlideCaptchaProps> = ({
  data,
  onValidate,
  onRefresh,
  loading = false,
  error,
  titleText,
  retryText,
  noDataAvailableText,
  backgroundAltText,
  pieceAltText,
  validatingText,
  sliderHandleText,
  instructionText,
}) => {
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [validating, setValidating] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  // Fixed container size to prevent resize issues
  const containerSize = { width: 350, height: 220 };
  const [imageLoaded, setImageLoaded] = useState(false);

  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const validationTokenRef = useRef<number>(0);
  const lastValidationTimeRef = useRef<number>(0);

  // Refs for drag state management
  const dragStateRef = useRef<{
    startX: number;
    initialPosition: number;
    maxSlide: number;
    finalPosition: number;
    isTouch: boolean;
  } | null>(null);

  // Ref for image dimension checking animation frame
  const dimensionCheckRef = useRef<number | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Fixed sizing based on consistent dimensions
  const { width, height } = containerSize;

  // Reset slider position when data changes
  useEffect(() => {
    if (data) {
      setSliderPosition(0);
      setStartTime(Date.now());
      setImageLoaded(false); // Reset image loaded state
    }
  }, [data]);

  // Remove dynamic sizing to prevent resize issues
  // Component now uses fixed dimensions for consistent behavior

  const validateSlide = useCallback(async (finalPosition?: number) => {
    if (!data) return;

    // Check if already validating with the state
    if (validating) return;

    // Increment token and store current value for this validation
    const currentToken = ++validationTokenRef.current;

    const currentPosition = finalPosition !== undefined ? finalPosition : sliderPosition;

    try {
      setValidating(true);
      const duration = Date.now() - startTime;

      // Calculate the original position accounting for scaling
      let originalX = currentPosition + (data.thumbX || 0);
      let originalY = data.thumbY || 0;

      if (imageDimensions.width > 0) {
        const scaleX = width / imageDimensions.width;
        // Convert display position back to original image coordinates
        originalX = Math.round(currentPosition / scaleX) + (data.thumbX || 0);
      }

      if (imageDimensions.height > 0) {
        // For Y coordinate, just use the original thumbY since we only moved horizontally
        originalY = data.thumbY || 0;
      }

      const point = {
        x: originalX,
        y: originalY,
      };

      const isValid = await onValidate(point, data.key, duration);

      // Only apply result if this is still the latest validation
      if (currentToken === validationTokenRef.current) {
        if (!isValid) {
          // Reset position on failed validation
          setSliderPosition(0);
          setStartTime(Date.now());
        }
      }
    } catch (err) {
      // Only apply error handling if this is still the latest validation
      if (currentToken === validationTokenRef.current) {
        // Reset position on error
        setSliderPosition(0);
        setStartTime(Date.now());
      }
    } finally {
      // Only clear validating state if this is still the latest validation
      if (currentToken === validationTokenRef.current) {
        setValidating(false);
      }
    }
  }, [data, sliderPosition, startTime, onValidate, validating, imageDimensions, width, height]);

  // Debounced validation wrapper to prevent rapid repeated triggers
  const debouncedValidateSlide = useMemo(() => {
    const DEBOUNCE_DELAY = 100; // 100ms debounce

    return (finalPosition?: number) => {
      const now = Date.now();
      if (now - lastValidationTimeRef.current < DEBOUNCE_DELAY) {
        return;
      }
      lastValidationTimeRef.current = now;
      validateSlide(finalPosition);
    };
  }, [validateSlide]);

  // Function to check image dimensions using requestAnimationFrame
  const checkImageDimensions = useCallback((img: HTMLImageElement) => {
    // Cancel any existing check
    if (dimensionCheckRef.current) {
      cancelAnimationFrame(dimensionCheckRef.current);
    }

    const checkDimensions = () => {
      const { naturalWidth, naturalHeight } = img;

      // Check if dimensions are available and non-zero
      if (naturalWidth > 0 && naturalHeight > 0) {
        setImageDimensions({ width: naturalWidth, height: naturalHeight });
        setImageLoaded(true);
        dimensionCheckRef.current = null;
      } else {
        // Continue checking on next frame
        dimensionCheckRef.current = requestAnimationFrame(checkDimensions);
      }
    };

    // Start the check
    dimensionCheckRef.current = requestAnimationFrame(checkDimensions);
  }, []);

  // Effect to manage document event listeners when dragging
  useEffect(() => {
    if (!isDragging || !dragStateRef.current) return;

    const dragState = dragStateRef.current;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragState.startX;
      const newPosition = Math.max(0, Math.min(dragState.maxSlide, dragState.initialPosition + deltaX));
      dragState.finalPosition = newPosition;
      setSliderPosition(newPosition);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const deltaX = touch.clientX - dragState.startX;
      const newPosition = Math.max(0, Math.min(dragState.maxSlide, dragState.initialPosition + deltaX));
      dragState.finalPosition = newPosition;
      setSliderPosition(newPosition);
      e.preventDefault();
    };

    const handleEnd = () => {
      setIsDragging(false);
      // Pass the final position directly to validation
      debouncedValidateSlide(dragState.finalPosition);
      dragStateRef.current = null;
    };

    if (dragState.isTouch) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
    } else {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
    }

    // Cleanup function that always removes listeners
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, debouncedValidateSlide]);

  // Effect to check image dimensions on mount or data change
  useEffect(() => {
    if (imageRef.current && data) {
      const img = imageRef.current;
      // Check if image is already complete (e.g., cached)
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        setImageLoaded(true);
      }
    }
  }, [data]);

  // Cleanup effect for animation frame
  useEffect(() => {
    return () => {
      if (dimensionCheckRef.current) {
        cancelAnimationFrame(dimensionCheckRef.current);
      }
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!data || validating) return;

    const startX = e.clientX;
    const initialPosition = sliderPosition;
    const maxSlide = width - 60; // 60px is slider width

    // Store drag state in ref
    dragStateRef.current = {
      startX,
      initialPosition,
      maxSlide,
      finalPosition: initialPosition,
      isTouch: false
    };

    setIsDragging(true);
    e.preventDefault();
  }, [data, sliderPosition, width, validating]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!data || validating) return;

    const touch = e.touches[0];
    const startX = touch.clientX;
    const initialPosition = sliderPosition;
    const maxSlide = width - 60;

    // Store drag state in ref
    dragStateRef.current = {
      startX,
      initialPosition,
      maxSlide,
      finalPosition: initialPosition,
      isTouch: true
    };

    setIsDragging(true);
    e.preventDefault();
  }, [data, sliderPosition, width, validating]);

  if (loading) {
    return (
      <CaptchaContainer width={width}>
        <LoadingContainer height={height}>
          <ActivityIndicator visible={true} />
        </LoadingContainer>
      </CaptchaContainer>
    );
  }

  if (error) {
    return (
      <CaptchaContainer width={width}>
        <ErrorMessage>
          {error}
          {onRefresh && (
            <button onClick={onRefresh} style={{ marginLeft: '10px' }}>
              {retryText}
            </button>
          )}
        </ErrorMessage>
      </CaptchaContainer>
    );
  }

  if (!data) {
    return (
      <CaptchaContainer width={width}>
        <ErrorMessage>{noDataAvailableText}</ErrorMessage>
      </CaptchaContainer>
    );
  }

  return (
    <CaptchaContainer width={width}>
      <CaptchaTitle>{titleText}</CaptchaTitle>
      <CaptchaWrapper>
        <ImageContainer width={width} height={height} ref={containerRef}>
          <img
            ref={imageRef}
            src={data.image.startsWith('data:') ? data.image : `data:image/png;base64,${data.image}`}
            alt={backgroundAltText}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              userSelect: 'none',
              pointerEvents: 'none'
            }}
            onLoad={(event) => {
              const img = event.currentTarget;
              // Check if image is already complete (loaded before onLoad fires)
              if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
                setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                setImageLoaded(true);
              } else {
                checkImageDimensions(img);
              }
            }}
          />

          {imageLoaded && (
            <PuzzlePiece
              style={{
                left: `${sliderPosition}px`,
                top: `${(() => {
                  if (imageDimensions.height > 0 && data.thumbY !== undefined) {
                    // Simple proportional scaling - let CSS handle the object-fit
                    const scaleY = (height / imageDimensions.height);
                    return Math.round((data.thumbY || 0) * scaleY);
                  }
                  return data.thumbY || 0;
                })()}px`,
                width: `${(() => {
                  if (imageDimensions.width > 0 && data.thumbWidth !== undefined) {
                    const scaleX = width / imageDimensions.width;
                    const scaledWidth = Math.round((data.thumbWidth || 0) * scaleX);
                    return scaledWidth - 1; // Reduce by 1px to fit better in the cut
                  }
                  return (data.thumbWidth || 60) - 1;
                })()}px`,
                height: `${(() => {
                  if (imageDimensions.height > 0 && data.thumbHeight !== undefined) {
                    const scaleY = height / imageDimensions.height;
                    const scaledHeight = Math.round((data.thumbHeight || 0) * scaleY);
                    return scaledHeight - 1; // Reduce by 1px to fit better in the cut
                  }
                  return (data.thumbHeight || 60) - 1;
                })()}px`,
              }}>
              <img
                src={data.thumb.startsWith('data:') ? data.thumb : `data:image/png;base64,${data.thumb}`}
                alt={pieceAltText}
                style={{
                  width: '100%',
                  height: '100%',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
              />
            </PuzzlePiece>
          )}
        </ImageContainer>

        <SliderContainer width={width}>
          <SliderTrack>
            <Slider
              ref={sliderRef}
              style={{ left: `${sliderPosition}px` }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              $isDragging={isDragging}
              disabled={validating}
            >
              <SliderText>
                {validating ? validatingText : sliderHandleText}
              </SliderText>
            </Slider>
            <SliderPrompt>
              {instructionText}
            </SliderPrompt>
          </SliderTrack>
        </SliderContainer>
      </CaptchaWrapper>
    </CaptchaContainer>
  );
};

export default SlideCaptcha;
